import React from 'react';
import SbEditable, { SbEditableContent } from 'storyblok-react';
import {
  BlokComponent as PillarPageComponent,
  NewStoryblokMetaData,
  StoryblokAsset,
  StoryblokAuthor,
  StoryblokCategories,
  StoryblokTags,
} from '../../types/blok.model';
import RenderAfterPageTransition from '../../old/components/RenderAfterPageTransition';
import defaultTheme, { darkTheme } from '../../styles/themeVariables';
import { ThemeProvider } from 'styled-components';
import { ThemeModeContextProvider } from '../../util/useThemeMode';
import { RichTextDocument } from '../components/RichText/StoryblokRichText';
import ContactFormModule from '../modules/ContactFormModule';
import PostHeaderModule from '../modules/PostHeaderModule';
import ScrollProgressBar from '../components/ScrollProgressBar';
import TableOfContents from '../components/TableOfContents';
import LongformTextModuleBlok from '../modules/LongformTextModule/LongformTextModule.blok';
import Spacer from '../components/Spacer';

interface PillarPageSchema extends NewStoryblokMetaData {
  light_header: boolean;
  title: string;
  author: StoryblokAuthor;
  category: StoryblokCategories;
  tags: StoryblokTags;
  hero_image: StoryblokAsset;
  hide_hero_image: boolean;
  content: RichTextDocument;
  hideHeroImage?: boolean;
  hideAuthor?: boolean;
  showProgress?: boolean;
  showTableOfContents?: boolean;
  body?: SbEditableContent[];
  bodyLower?: SbEditableContent[];
  bodySpacing?: string;
  bodyLowerSpacing?: string;
  tableOfContents?: {
    id: string;
    title: string;
  }[];
}

const PillarPageNew: PillarPageComponent<PillarPageSchema> = ({
  blok,
  Dynamic,
}) => {
  return (
    <SbEditable content={blok}>
      <ThemeModeContextProvider themeMode={'light'}>
        <ThemeProvider theme={defaultTheme}>
          <PostHeaderModule
            key={blok._uid}
            title={blok.title}
            mobile={blok.hero_image?.filename}
            tablet={blok.hero_image?.filename}
            desktop={blok.hero_image?.filename}
            alt={blok.hero_image?.alt}
            categories={blok.category}
            tags={blok.tags}
            authors={[blok.author]}
            hideHeroImage={blok.hideHeroImage}
            hideAuthor={blok.hideAuthor}
          />

          <ScrollProgressBar show={blok.showProgress}>
            <TableOfContents
              show={blok.showTableOfContents}
              linksList={blok.tableOfContents}
            >
              <RenderAfterPageTransition>
                <Spacer className={`item-0 ${blok.bodySpacing ?? 'minimal'} `}>
                  <LongformTextModuleBlok blok={blok} Dynamic={Dynamic} />
                </Spacer>
                {blok.body?.map((insideBlok, index) => (
                  <Spacer
                    key={insideBlok._uid}
                    className={`item-${index} ${
                      insideBlok.spacing ?? blok.bodySpacing ?? 'minimal'
                    } `}
                  >
                    <Dynamic blok={insideBlok} />
                  </Spacer>
                ))}
              </RenderAfterPageTransition>
            </TableOfContents>
          </ScrollProgressBar>
        </ThemeProvider>
      </ThemeModeContextProvider>

      <ThemeModeContextProvider themeMode={'dark'}>
        <ThemeProvider theme={darkTheme(defaultTheme)}>
          {blok.bodyLower?.map((insideBlok, index) => (
            <Spacer
              key={insideBlok._uid}
              className={`item-${index} ${
                insideBlok.spacing ?? blok.bodyLowerSpacing ?? 'none'
              } `}
            >
              <Dynamic blok={insideBlok} />
            </Spacer>
          ))}
          <ContactFormModule
            form_data={'7dfec556-192c-483f-bc39-246e44bb1f7b'}
            tagline={'READY TO GET STARTED?'}
            title={
              'Get in touch today and accelerate how your most important work gets done.'
            }
            subtitle={
              'Meet the team, discuss your ideas with our experts and receive a proposal for your project.'
            }
            light_theme={false}
          />
        </ThemeProvider>
      </ThemeModeContextProvider>
    </SbEditable>
  );
};

/**
 * Handle fallback to old pillar page
 */
import PillarPageOld from './PillarPageOld';

interface PageWithSwitcher extends PillarPageSchema {
  useLatestDesign: boolean;
}

const PageSwitcher: PillarPageComponent<PageWithSwitcher> = ({
  blok,
  Dynamic,
}) => {
  return blok.useLatestDesign === true ? (
    <PillarPageNew blok={blok} Dynamic={Dynamic} />
  ) : (
    <PillarPageOld blok={blok} Dynamic={Dynamic} />
  );
};

export default React.memo(PageSwitcher);
